import {
  Locutus,
  listGuidancePrimitiveApi,
  PlanAsset,
  postGuidancePrimitiveApi
} from 'api-validations/locutus'
import { DEFAULT_GUIDANCE_PROPERTIES, GuidancePrimitive } from 'api-validations/locutus/guidance'
import { useOrgCodeOrThrow } from 'shared-ui/active-operation'
import { useApiClient, useMutation, useQueryClient } from 'shared-ui/api-provider'
import { useMapContext } from 'shared-ui/map'
import { ApiError } from 'utils'

type PrimitiveAssetNames = {
  name: string
  featId: string
  swathWidthFt?: number
  rowSpacingFt?: number
}

// Locally-tracked view of a just-saved primitive. Matches the list-endpoint
// item shape (minus fields we don't need here) so consumers can treat freshly
// saved items and refetched items uniformly. `featId` is included so callers
// can pair a saved primitive back to the draw-tool feature that produced it
// (useful when saving multiple primitives at once — Promise.all order follows
// draw-tool iteration order, not the input order).
export type SavedPrimitive = Pick<Locutus.GuidancePrimitiveAsset, 'id' | 'name' | 'data'> & {
  featId: string
}

/**
 * Mutation for creating a GeoJSON-based boundary asset
 */
export const useSavePrimitive = (
  fieldId: string,
  onSuccess?: (results: SavedPrimitive[]) => void
) => {
  const orgCode = useOrgCodeOrThrow()
  const apiClient = useApiClient()
  const queryClient = useQueryClient()
  const { map } = useMapContext()

  return useMutation({
    mutationFn: async (assets: PrimitiveAssetNames[]) => {
      const assetByFeatId = new Map(assets.map((a) => [a.featId, a]))
      // Only save features the caller explicitly listed. The draw tool can hold
      // per-region references to already-saved primitives (added by the Regions
      // tab's edge/existing selects) — those must not be re-POSTed here.
      // `getGuidancePrimitives` returns schema-shaped features (Point for
      // AHeading/RadialPivot), so no reshaping is needed here.
      const infill = map.drawTool.getGuidancePrimitives().features.filter((feat) => {
        const id = feat.id
        return id !== undefined && assetByFeatId.has(String(id))
      })
      try {
        return await Promise.all(
          infill.map(async (feat) => {
            const { zone, ...properties } = (feat.properties ?? {}) as {
              zone: string
              type: GuidancePrimitive
              headingDeg?: number
              radiusFt?: number
              [key: string]: unknown
            }

            const asset = assetByFeatId.get(String(feat.id))
            const name = asset?.name ?? properties.type

            // Zod-parse the body so `data`'s discriminated union narrows to a
            // specific literal type — the local `properties.type` is widened to
            // the full GuidancePrimitive enum.
            const body = postGuidancePrimitiveApi.requestBodySchema.parse({
              type: PlanAsset.GuidancePrimitive,
              name,
              fieldId,
              data: {
                type: properties.type,
                primitive: {
                  ...feat,
                  properties: {
                    guidance: {
                      ...DEFAULT_GUIDANCE_PROPERTIES,
                      ...properties,
                      ...(asset?.swathWidthFt !== undefined && {
                        swathWidthFt: asset.swathWidthFt
                      }),
                      ...(asset?.rowSpacingFt !== undefined && {
                        rowSpacingFt: asset.rowSpacingFt
                      })
                    }
                  }
                }
              }
            })

            const response = await apiClient.post(postGuidancePrimitiveApi.pathFor({ orgCode }), {
              body
            })

            const { id } = postGuidancePrimitiveApi.responseSchema.parse(response.body)
            return {
              id,
              name,
              data: body.data,
              featId: String(feat.id)
            } satisfies SavedPrimitive
          })
        )
      } catch (e) {
        if (e instanceof ApiError) {
          let msg: string | undefined
          try {
            const parsed = JSON.parse(e.responseText) as string | { message?: string }
            msg =
              typeof parsed === 'string'
                ? (JSON.parse(parsed) as { message?: string }).message
                : parsed.message
          } catch {}
          if (msg !== undefined && msg !== '') throw new Error(msg)
          const stripped = e.responseText.replaceAll(`"`, ``)
          throw new Error(stripped || e.message)
        }
        throw e
      }
    },
    onSuccess: (results) => {
      onSuccess?.(results)
      queryClient.invalidateQueries({
        queryKey: listGuidancePrimitiveApi.queryKey({ orgCode, fieldId })
      })
    }
  })
}
